import { useParams } from '@tanstack/react-router';

import { Tag } from '../../cdl/Tag/Tag';
import { Text } from '../../cdl/Text/Text';
import { AdvancedError } from '../../common/Error/AdvancedError';
import { HeaderWithLine } from '../../common/HeaderWithLine/HeaderWithLine';
import { translate } from '../../common/helpers/translate.helper';
import { useDocumentTitle } from '../../common/hooks/useDocumentTitle';
import { useRole } from '../../common/hooks/useRole';
import { LoadingIndicator } from '../../common/LoadingIndicator/LoadingIndicator';
import { Page } from '../../common/Page/Page';
import { Box } from '../../common/ui/Box';
import { ArchiveRestoreButton } from '../components/ArchiveRestoreButton';

import { AgentContactDataSheet } from './components/AgentContactDataSheet';
import { AgentDetailsDataSheet } from './components/AgentDetailsDataSheet';
import { EditAgentButton } from './components/EditAgentButton';
import { useAgentDetails } from './hooks/useAgentDetails';

export const AgentDetailPage = () => {
    const { id } = useParams({ from: '/_app/agents/$id' });
    const role = useRole();
    const agentQuery = useAgentDetails(id);

    const agent = agentQuery.data;

    useDocumentTitle(translate('page.agentDetails', { name: agent?.name || '' }));

    if (agentQuery.isPending) {
        return (
            <Page>
                <LoadingIndicator variant="page" />
            </Page>
        );
    }

    if (agentQuery.isError || !agent) {
        return (
            <Page>
                <AdvancedError />
            </Page>
        );
    }

    const contactAvailable =
        agent.contact?.name || agent.contact?.phoneNumber || agent.contact?.emailAddress || agent.contact?.faxNumber;

    return (
        <Page>
            <Box display="flex" flexDirection="column" gap={48}>
                <Box display="flex" justifyContent="space-between" alignItems="center" gap={16}>
                    <Box display="flex" flexDirection="row" gap={16} alignItems="center">
                        <Text variant="headline" as="h1">
                            {agent.name}
                        </Text>
                        {!agent.active ? <Tag variant="default">{translate('agents.detail.archivedTag')}</Tag> : null}
                        {agent.source ? <Tag variant="default">{translate('agents.detail.importedTag')}</Tag> : null}
                    </Box>
                    {role.hasWriteRights() && !agent.source ? (
                        <Box display="flex" gap={8}>
                            <ArchiveRestoreButton agent={agent} />
                            <EditAgentButton agent={agent} />
                        </Box>
                    ) : null}
                </Box>

                <Box display="grid" gap={48} gridTemplateColumns={'1fr' + (contactAvailable ? ' 1fr' : '')}>
                    <Box display="flex" flexDirection="column" gap={16}>
                        <HeaderWithLine>
                            <Text variant="title" as="h2">
                                {translate('agents.detail.sectionHeadline')}
                            </Text>
                        </HeaderWithLine>
                        <AgentDetailsDataSheet agent={agent} />
                    </Box>
                    {agent.contact && contactAvailable ? (
                        <Box display="flex" flexDirection="column" gap={16}>
                            <HeaderWithLine>
                                <Text variant="title" as="h2">
                                    {translate('agents.detail.contact.headline')}
                                </Text>
                            </HeaderWithLine>
                            <AgentContactDataSheet contact={agent.contact} />
                        </Box>
                    ) : null}
                </Box>
            </Box>
        </Page>
    );
};
